import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import Navbar from "@/components/Navbar";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import MarketplacePage from "@/pages/MarketplacePage";
import DecksPage from "@/pages/DecksPage";
import NewDeckPage from "@/pages/NewDeckPage";
import DeckDetailsPage from "@/pages/DeckDetailsPage";
import DeckEditPage from "@/pages/DeckEditPage";
import StudyPage from "@/pages/StudyPage";
import ProfilePage from "@/pages/ProfilePage";
import { HelmetProvider } from "react-helmet-async";
import { cn } from "@/lib/utils";

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuthStore();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function AppContent() {
  const location = useLocation();
  const isStudyPage = location.pathname.includes("/study");

  return (
    <div className="min-h-screen bg-background font-sans flex flex-col">
          {!isStudyPage && <Navbar />}
          <main className={cn(
            "flex-1",
            !isStudyPage ? "container mx-auto px-4 py-8" : "h-screen w-screen overflow-hidden"
          )}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />

              {/* Protected Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/analytics" 
                element={
                  <ProtectedRoute>
                    <AnalyticsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/marketplace" 
                element={
                  <ProtectedRoute>
                    <MarketplacePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/decks" 
                element={
                  <ProtectedRoute>
                    <DecksPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/decks/new" 
                element={
                  <ProtectedRoute>
                    <NewDeckPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/decks/:id" 
                element={
                  <ProtectedRoute>
                    <DeckDetailsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/decks/:id/edit" 
                element={
                  <ProtectedRoute>
                    <DeckEditPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/decks/:id/study" 
                element={
                  <ProtectedRoute>
                    <StudyPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          
          {!isStudyPage && (
            <footer className="border-t py-12 mt-20">
              <div className="container mx-auto px-4 text-center text-muted-foreground font-medium">
                <p>© 2026 ITG Viet Flashcard Platform. Built with Vite & React.</p>
              </div>
            </footer>
          )}
        </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AppContent />
    </HelmetProvider>
  );
}

export default App;
